import { GraduationCap, MapPin, DollarSign, School, BookOpen } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface University {
  name: string;
  country: string;
  programs: string[];
  hasScholarship: boolean;
  annualTuition: number;
  scholarshipDetails?: string;
  websiteUrl?: string;
}

interface ScholarshipResultsProps {
  universities: University[];
  isLoading?: boolean;
}

const ScholarshipResults = ({ universities, isLoading = false }: ScholarshipResultsProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  const totalPages = Math.ceil(universities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const visibleUniversities = universities.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  // Function to validate and format university website URLs
  const getValidWebsiteUrl = (university: University): string => {
    // If websiteUrl is provided and valid, use it
    if (university.websiteUrl && isValidUrl(university.websiteUrl)) {
      return university.websiteUrl;
    }
    
    // Generate a standardized URL based on university name
    const name = university.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    // Determine appropriate domain extension based on country
    let domain = ".edu";
    if (university.country) {
      const country = university.country.toLowerCase();
      if (country.includes("united kingdom") || country.includes("uk")) {
        domain = ".ac.uk";
      } else if (country.includes("canada")) {
        domain = ".ca";
      } else if (country.includes("australia")) {
        domain = ".edu.au";
      } else if (country.includes("germany")) {
        domain = ".de";
      } else if (country.includes("france")) {
        domain = ".fr";
      } else if (country.includes("japan")) {
        domain = ".jp";
      } else if (country.includes("china")) {
        domain = ".cn";
      } else if (country.includes("india")) {
        domain = ".ac.in";
      }
    }
    
    return `https://www.${name}${domain}`;
  };
  
  // Function to check if a URL is valid
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, index) => (
          <Card key={index} className="border shadow-sm animate-pulse">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <School className="h-5 w-5 text-gray-300" />
                <Skeleton className="h-6 w-3/4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-300" />
                  <Skeleton className="h-4 w-1/4" />
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-gray-300" />
                  <Skeleton className="h-4 w-2/5" />
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-10 w-1/4" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  if (universities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No universities found matching your criteria
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <School className="h-5 w-5 text-blue-600" />
        Found {universities.length} Universities with Matching Programs
      </h2>
      
      <div className="space-y-6">
        {visibleUniversities.map((uni, index) => (
          <Card key={`${uni.name}-${index}`} className="border shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <Link to={`/scholarship-details/${encodeURIComponent(uni.name)}`} className="inline-block">
                    <CardTitle className="text-xl text-blue-600 hover:underline flex items-center gap-2">
                      <GraduationCap className="h-5 w-5" />
                      {uni.name}
                    </CardTitle>
                  </Link>
                  <CardDescription className="flex items-center gap-1 mt-1">
                    <BookOpen className="h-3.5 w-3.5" />
                    {uni.programs.join(", ")}
                  </CardDescription>
                </div>
                {uni.hasScholarship && (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    Scholarship Available
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-1 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>{uni.country}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-600">
                  <DollarSign className="h-4 w-4" />
                  <span>Annual Tuition: ${uni.annualTuition.toLocaleString()}</span>
                </div>
                {uni.scholarshipDetails && (
                  <p className="text-sm text-gray-700 border-l-2 border-blue-500 pl-3 py-1 bg-blue-50 rounded-sm mt-1">
                    {uni.scholarshipDetails}
                  </p>
                )}
                <div className="flex items-center mt-3">
                  <Link to={`/scholarship-details/${encodeURIComponent(uni.name)}`}>
                    <Button variant="default" size="sm">
                      View Details
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            {currentPage > 1 && (
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    goToPage(currentPage - 1);
                  }}
                />
              </PaginationItem>
            )}
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  href="#"
                  isActive={page === currentPage}
                  onClick={(e) => {
                    e.preventDefault();
                    goToPage(page);
                  }}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            {currentPage < totalPages && (
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    goToPage(currentPage + 1);
                  }}
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default ScholarshipResults;
